import React, { useEffect, useState } from "react";
import { FormLayout } from "@components/Forms/FormLayout/FormLayout";
import { FormMessages } from "@components/Forms/FormMessages/FormMessages";
import { UserInfoFields } from "@components/Forms/UserInfoFields/UserInfoFields";
import { useFormSubmit } from "@hooks/useFormSubmit";
import { SocialAuth } from "@components/SocialAuth/SocialAuth";
import userAuthService from "@api/userAuthService";
import feedbackService from "@api/feedbackService";
import { FormPersistence } from "@utils/FormPersistenceUtil";
// import debugService from "@api/debugService";

import TextField from "@components/TextField/TextField";
import SubmitButton from "@components/Buttons/SubmitButton/SubmitButton";
import styles from "./QuestionForm.module.css";

export interface QuestionFormData {
  question: string;
  name: string;
  email: string;
}

const initialFormData: QuestionFormData = {
  question: "",
  name: "",
  email: "",
};

interface QuestionFormProps {
  productId?: string;
  productName?: string;
  onModalClose?: () => void;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  productId,
  productName = "",
  onModalClose,
}) => {
  const [isFormVisible, setIsFormVisible] = useState<boolean>(true);
  const [isSuccessMessageVisible, setIsSuccessMessageVisible] =
    useState<boolean>(false);
  const [socialAuthError, setSocialAuthError] = useState<string>("");

  // Загружаем сохраненные данные из localStorage
  const loadSavedFormData = (): QuestionFormData => {
    const savedData = FormPersistence.getQuestionForm(productId);
    if (savedData) {
      return {
        ...initialFormData,
        ...savedData,
      };
    }

    // Если нет сохраненных данных, попробуем взять из данных пользователя
    const userData = userAuthService.getUserData();
    if (userData) {
      return {
        ...initialFormData,
        name: userData.name || "",
        email: userData.email || "",
      };
    }

    return initialFormData;
  };

  const {
    formData,
    setFormData,
    handleInputChange,
    isSubmitting,
    submitError,
    submitSuccess,
    handleSubmit: submitForm,
    isAttemptedSubmit,
  } = useFormSubmit<QuestionFormData>({
    initialData: loadSavedFormData(),
    validateForm: (data) => {
      return (
        data.question.trim() !== "" &&
        data.name.trim() !== "" &&
        data.email.trim() !== ""
      );
    },
    onSubmit: async (data) => {
      // debugService.info("Submitting question form", {
      //   name: data.name,
      //   email: data.email,
      //   productId,
      // });

      try {
        // Отправить вопрос
        await feedbackService.submitQuestion({
          name: data.name,
          email: data.email,
          product_id: productId,
          product_name: productName,
          text: data.question,
          optional_fields: {},
        });

        // Очищаем сохраненные данные после успешной отправки
        FormPersistence.clearQuestionForm();

        return {
          success: true,
          message: "Спасибо! Ваш вопрос успешно отправлен",
        };
      } catch (error) {
        console.log("Error in question submission: ", error);
        // debugService.error("Error in question submission", { error });
        return {
          success: false,
          message: "Произошла ошибка при отправке вопроса",
        };
      }
    },
    successMessage: "Спасибо! Ваш вопрос успешно отправлен",
    errorMessage: "Произошла ошибка при отправке вопроса",
  });

  // Сохраняем данные формы при каждом изменении
  useEffect(() => {
    // Не сохраняем пустую форму или если уже отправлено успешно
    if (!submitSuccess && formData.question) {
      FormPersistence.saveQuestionForm(formData, productId);
    }
  }, [formData, submitSuccess, productId]);

  const [formErrors] = useState<{ form?: string }>({});

  // Проверка валидности формы
  const isFormValid = () => {
    const isQuestionValid = formData.question.trim() !== "";
    const isNameValid = formData.name.trim() !== "";
    const isEmailValid = validateEmail(formData.email);
    return isQuestionValid && isNameValid && isEmailValid;
  };

  // Обработка отправки формы
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFormValid()) {
      submitForm(e as React.FormEvent<HTMLFormElement>);
    }
  };

  // Закрыть форму сразу после успешной отправки и показать сообщение об успехе
  useEffect(() => {
    if (submitSuccess) {
      // Сразу закрыть форму и показать сообщение
      setIsFormVisible(false);
      setIsSuccessMessageVisible(true);

      // Закрыть сообщение об успехе через 3 секунды
      const timer = setTimeout(() => {
        setIsSuccessMessageVisible(false);
        // Close the parent modal
        if (onModalClose) {
          onModalClose();
        }
      }, 3000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [submitSuccess, onModalClose]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Обработчик успешной авторизации через соцсеть
  const handleSocialAuth = (userData: any) => {
    setFormData((prev) => ({
      ...prev,
      name: userData.name || prev.name,
      email: userData.email || prev.email,
    }));
  };

  // Обработчик ошибки авторизации через соцсеть
  const handleSocialAuthError = (error: string) => {
    setSocialAuthError(error);
    setTimeout(() => setSocialAuthError(""), 5000);
  };

  return (
    <>
      {isFormVisible ? (
        <FormLayout title="Вопрос по товару" onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <TextField
              title="Вопрос"
              required
              placeholder="Напишите Ваш вопрос о товаре"
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              multiline
              rows={3}
              error={
                isAttemptedSubmit && formData.question.trim() === ""
                  ? "Необходимо заполнить поле 'Вопрос'"
                  : ""
              }
            />
          </div>

          <div className={styles.userInfoSection}>
            <UserInfoFields
              name={formData.name}
              email={formData.email}
              onNameChange={(value) =>
                setFormData((prev) => ({ ...prev, name: value }))
              }
              onEmailChange={(value) =>
                setFormData((prev) => ({ ...prev, email: value }))
              }
              isAttemptedSubmit={isAttemptedSubmit}
            />
          </div>

          <div className={styles.socialAuthSection}>
            {socialAuthError && <FormMessages error={socialAuthError} />}
            <SocialAuth
              onAuth={handleSocialAuth}
              onError={handleSocialAuthError}
            />
          </div>

          <div className={styles.fixedSubmitSection}>
            <FormMessages
              error={submitError || formErrors.form}
              success={submitSuccess}
            />

            <SubmitButton
              text="Отправить вопрос"
              loading={isSubmitting}
              disabled={!isFormValid() || isSubmitting}
            />
          </div>
        </FormLayout>
      ) : isSuccessMessageVisible ? (
        <div className={styles.successContainer}>
          <h2 className={styles.successTitle}>Спасибо!</h2>
          <p className={styles.successMessage}>
            Ваш вопрос успешно отправлен и будет передан специалистам магазина.
          </p>
        </div>
      ) : null}
    </>
  );
};

export default QuestionForm;
